<script setup>
import { computed } from 'vue';

import RequiredFieldsRule from '@/components/RuleEngine/Attributes/RequiredFieldsRule.vue';
import GbcScoreRule from '@/components/RuleEngine/Attributes/GbcScoreRule.vue';
import AgeLimitsRule from '@/components/RuleEngine/Attributes/AgeLimitsRule.vue';
import AgeGapRule from '@/components/RuleEngine/Attributes/AgeGapRule.vue';
import AmountToFinanceRule from '@/components/RuleEngine/Attributes/AmountToFinanceRule.vue';
import TdsrRatioValueRule from '@/components/RuleEngine/Attributes/TdsrRatioValueRule.vue';
import Crp30ScoreRule from '@/components/RuleEngine/Attributes/Crp30ScoreRule.vue';
import ProvincialAcceptanceRule from '@/components/RuleEngine/Attributes/ProvincialAcceptanceRule.vue';
import CustomerTypesRule from '@/components/RuleEngine/Attributes/CustomerTypesRule.vue';
import LtdValueRule from '@/components/RuleEngine/Attributes/LtdValueRule.vue';
import BooleanCheckRule from '@/components/RuleEngine/Attributes/BooleanCheckRule.vue';

const props = defineProps({
    attributeKey: { type: String, required: true },
    mappedFields: { type: Array, default: () => [] },
    modelValue: { type: [Object, Array, String, Number, Boolean, null], default: null },
    errors: { type: [Array, Object], default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'touched']);

const v = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
});

// AttributeKey -> Component mapping for non-boolean rules 
const COMPONENT_MAP = {
    required_fields: RequiredFieldsRule,
    gbc_score: GbcScoreRule,
    age_limits: AgeLimitsRule,
    age_gap: AgeGapRule,
    amount_to_finance: AmountToFinanceRule,
    tdsr_ratio_value: TdsrRatioValueRule,
    crp30_score: Crp30ScoreRule,
    provincial_acceptance: ProvincialAcceptanceRule,
    customer_types: CustomerTypesRule,
    ltd_value: LtdValueRule,
};

// AttributeKeys for boolean rules and their corresponding label keys
const BOOLEAN_RULE_LABELS = {
    cohabitation_check: 'label.cohabitation_requirement',
    consumer_statements_check: 'label.consumer_statements_ok',
    bankruptcy_indicator_check: 'label.bankruptcy_indicator_ok',
    provincial_match: 'label.province_match',
    shopping_borrowers: 'label.shopping_borrowers_ok',
    material_change: 'label.material_change_ok',
};

const resolvedComponent = computed(() => COMPONENT_MAP[props.attributeKey] ?? null);

const isBooleanRule = computed(() => Object.prototype.hasOwnProperty.call(BOOLEAN_RULE_LABELS, props.attributeKey));

const booleanLabelKey = computed(() => BOOLEAN_RULE_LABELS[props.attributeKey] ?? null);

</script>

<template>
    
<component
    :is="resolvedComponent"
    v-if="resolvedComponent"
    v-model="v"
    :mapped-fields="mappedFields" 
    :errors="errors" 
    @touched="$emit('touched')" />

<BooleanCheckRule
    v-else-if="isBooleanRule"
    v-model="v"
    :label-key="booleanLabelKey" 
    :errors="errors"
    @touched="$emit('touched')" />

<div v-else class="text-sm opacity-70">
    {{ $t('label.unsupported_attribute') }}: {{ attributeKey }}
</div>
</template>
